// Import-free on purpose: the node:test runner loads this file directly.

/** MarsEDV ticket-shop hosts a handoff may target. Exact match, never a suffix match. */
export const PULSE_HANDOFF_HOSTS: readonly string[] = ['kinotickets.express'];

export const HANDOFF_PARAM = 'cin_handoff';

/** Prefetch and prerender requests are not a visitor's click and must not mint a handle. */
export function isSpeculativeRequest(headers: Headers): boolean {
    return (
        /prefetch|prerender/i.test(headers.get('sec-purpose') ?? '') ||
        /prefetch/i.test(headers.get('purpose') ?? '') ||
        /preview/i.test(headers.get('x-purpose') ?? '') ||
        /prefetch/i.test(headers.get('x-moz') ?? '') ||
        headers.has('next-router-prefetch') ||
        headers.has('rsc')
    );
}

export function parseHandoffTarget(to: string | null): URL | null {
    if (!to) {
        return null;
    }

    let url: URL;
    try {
        url = new URL(to);
    } catch {
        return null;
    }

    if (url.protocol !== 'https:' || url.port !== '' || url.username !== '' || url.password !== '') {
        return null;
    }

    return PULSE_HANDOFF_HOSTS.includes(url.hostname) ? url : null;
}

export function isHandoffTarget(url: string): boolean {
    return parseHandoffTarget(url) !== null;
}

/**
 * What the browser's `ticket_click` carries, so the server-side click reaches the per-movie and
 * per-showing aggregates. Every value depends on the showing and the page only, never on the visitor:
 * the program HTML must stay identical for all visitors.
 */
export type HandoffAttribution = {
    showingId?: number;
    movieId?: number | null;
    contentId?: number;
    showingTagIds?: readonly number[];
    page?: string;
};

export const MAX_HANDOFF_TAGS = 20;
/** Mirrors the package's context guard on `page` (50 characters). */
export const MAX_HANDOFF_PAGE_LENGTH = 50;

export function handoffHref(ticketUrl: string, attribution: HandoffAttribution = {}): string {
    const params = [`to=${encodeURIComponent(ticketUrl)}`];
    const { showingId, movieId, contentId, showingTagIds, page } = attribution;

    if (showingId) params.push(`showing=${showingId}`);
    if (movieId) params.push(`movie=${movieId}`);
    if (contentId) params.push(`content=${contentId}`);
    if (showingTagIds?.length) params.push(`tags=${showingTagIds.join(',')}`);
    if (page) params.push(`page=${encodeURIComponent(page)}`);

    return `/api/pulse/ticket?${params.join('&')}`;
}

function parsePositiveInt(value: string | null): number | undefined {
    if (value === null || !/^[1-9][0-9]{0,9}$/.test(value)) {
        return undefined;
    }
    const number = Number(value);

    return Number.isSafeInteger(number) && number <= 2147483647 ? number : undefined;
}

/** Strict, lenient-on-failure: an invalid value is dropped, never an error. */
export function parseHandoffAttribution(params: URLSearchParams): HandoffAttribution {
    const attribution: HandoffAttribution = {};

    const showingId = parsePositiveInt(params.get('showing'));
    const movieId = parsePositiveInt(params.get('movie'));
    const contentId = parsePositiveInt(params.get('content'));
    if (showingId) attribution.showingId = showingId;
    if (movieId) attribution.movieId = movieId;
    if (contentId) attribution.contentId = contentId;

    const rawTags = params.get('tags');
    if (rawTags !== null) {
        const parts = rawTags.split(',');
        const tags = parts.map(parsePositiveInt);
        if (parts.length <= MAX_HANDOFF_TAGS && tags.every((tag) => tag !== undefined)) {
            attribution.showingTagIds = tags as number[];
        }
    }

    const page = params.get('page');
    if (page !== null && page.length <= MAX_HANDOFF_PAGE_LENGTH && /^[\w/][\w .:/-]*$/.test(page)) {
        attribution.page = page;
    }

    return attribution;
}

/**
 * Appends the handle to the vendor's URL as text, so its own query is not re-serialized
 * (`%20` stays `%20`, a bare `?a` stays `?a`). Any `cin_handoff` already present is removed first.
 */
export function withHandoffParam(target: URL, handle: string): string {
    const href = target.href;
    const hashIndex = href.indexOf('#');
    const fragment = hashIndex === -1 ? '' : href.slice(hashIndex);
    const withoutFragment = hashIndex === -1 ? href : href.slice(0, hashIndex);
    const queryIndex = withoutFragment.indexOf('?');
    const base = queryIndex === -1 ? withoutFragment : withoutFragment.slice(0, queryIndex);
    const kept =
        queryIndex === -1
            ? []
            : withoutFragment
                  .slice(queryIndex + 1)
                  .split('&')
                  .filter((pair) => pair !== '' && pair.split('=')[0] !== HANDOFF_PARAM);

    return `${base}?${[...kept, `${HANDOFF_PARAM}=${handle}`].join('&')}${fragment}`;
}

/** 128 random bits as 32 lowercase hex characters. */
export function newHandoffHandle(): string {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);

    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
